import { useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { TONE_CLASS } from './shell/agentTones'

/**
 * Confirm your email right here: the 6-digit code from the verification email (the same email also has a one-click
 * confirm link). Used where a verified account is required to continue (joining a company, connecting an assistant), so
 * nobody is sent to another page. onVerified receives the server's answer (it says `joined` when a pending company join
 * completed on verification).
 */
export default function VerifyCodeInline({ onVerified }) {
  const { user, verifyEmail, resendOtp } = useAuth()
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [resent, setResent] = useState(false)
  // A new code can be sent once a minute (the server's rule, as on the verify page); the first code was just sent.
  const [wait, setWait] = useState(60)
  useEffect(() => {
    if (wait <= 0) return undefined
    const t = setTimeout(() => setWait((w) => w - 1), 1000)
    return () => clearTimeout(t)
  }, [wait])

  const submit = async (e) => {
    e.preventDefault()
    setBusy(true); setError('')
    try {
      const res = await verifyEmail(code.trim())
      if (res?.data?.verified || res?.verified) onVerified?.(res?.data ?? res)
      else setError('That code did not work. Check it and try again.')
    } catch (err) {
      setError(err?.message || 'That code did not work. Check it and try again.')
    } finally { setBusy(false) }
  }
  const resend = async () => {
    if (wait > 0) return
    setResent(false); setError('')
    try {
      await resendOtp()
      setResent(true)
      setWait(60)
    } catch (err) {
      const seconds = Number(String(err?.message ?? '').match(/(\d+)\s*second/)?.[1])
      if (err?.status === 429 && seconds > 0) setWait(seconds)
      else setError('Could not send a new code. Try again in a minute.')
    }
  }

  return (
    <form onSubmit={submit} data-verify-code>
      <label htmlFor="verify-code" className="mt-6 block text-sm font-semibold text-gray-900">Code from the email we sent to {user?.email}</label>
      <input id="verify-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
        className="mt-2 w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-center text-lg font-semibold tracking-[0.4em] outline-none focus:border-primary-400" />
      {error && <div role="alert" className={`mt-4 rounded-xl border p-3 text-sm ${TONE_CLASS.alert}`}>{error}</div>}
      <button type="submit" disabled={busy || code.length !== 6}
        className="mt-4 flex w-full items-center justify-center rounded-xl bg-primary-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60">
        {busy ? 'Checking…' : 'Confirm'}
      </button>
      <p className="mt-3 text-center text-xs text-gray-500">
        Or use the Confirm button in that email.{' '}
        {wait > 0
          ? <span data-resend-wait>{resent ? 'New code sent. ' : ''}You can ask for a new code in {wait}s.</span>
          : <button type="button" onClick={resend} className="font-semibold text-primary-700 hover:text-primary-800">Send a new code</button>}
      </p>
    </form>
  )
}
